import SlideModel from './slide-model';
import { observable } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { IMasterExtensions, IMasterModel, MasterModelSdkTypeV3 } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { transaction } from '@src/slide/domain/slide-app';
import type { IPresentationModel } from './Interface/presentation-model.interface';
import type { IElementBaseModel } from './Interface/base/element-base-model.interface';
import { TDWOperationType, TDWModuleType, TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { FOOTER, TITLE, BODY, DATE, NUMBER } from '@src/slide/domain/ui-model/constant/place-holder';
import {
    MasterPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/page/master-property-converter';
import { IThemeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import { AddPlaceholderType, IMasterDataModel, STPlaceholderType } from '@tencent/slide-sdk-model';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';

const LAYOUT_LIST_CHANGE = 'layoutList';

export default class MasterModel extends SlideModel implements IMasterModel {
    public static convertDataUiToSdk(uiData, sdkData, rawUiData) {
        super.convertDataUiToSdk(uiData, sdkData, rawUiData);
    }

    public static async convertDataSdkToUi(sdkData, uiData, rawSdkData, changeInfo) {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData, changeInfo);
        MasterPropertyConverter.convertPartialDataFromSdkToUi(sdkData.props, uiData);
        // 3.0 layout的父节点是master，变更通知也从presentation中变到了master，当移动时，只会触发master的convert
        // 而layout的convert原来是在presentation中，现在需要在此处触发
        if (changeInfo?.diffs?.childList?.key === LAYOUT_LIST_CHANGE) {
            uiData.parent.convertMasterAndLayoutSdkDataToUiData();
        }
    }

    public readonly isMaster = true;

    // 是否是SDK提供的预加载数据
    @observable preload = false;
    @observable public layoutList = [];
    declare public sdkData: IMasterDataModel | null;
    public titleTextStyleList;
    public bodyTextStyleList;
    public otherTextStyleList;
    public themeId;
    public clrMap;
    public extensions: IMasterExtensions;

    public autoConvertChildren = true;
    public needConvertChildren = true;   // 是否需要转换子元素

    public constructor() {
        super();
        this.type = UIModelType.MASTER;
    }

    public get themeName() {
        return this.getTheme()?.name || '';
    }

    public getRelationLayoutModel() {
        return undefined;
    }

    // @ts-ignore
    public getRelationMasterModel() {
        return this;
    }

    public bindRelationObserver() {}

    public async bindSDK(sdkData): Promise<void> {
        super.bindSDK(sdkData);
        if (this.needConvertChildren && this.presentationRef) {
            this.needConvertChildren = this.presentationRef.needConvertChildren;
        }
    }

    public async convertSdkToUiChildrenLogic(rawSdkData, convertRemoveChangeset = false) {
        if (!this.needConvertChildren) return;

        const convertPromises: Promise<void>[] = [];
        convertPromises.push(this.getChildrenConvertPromise(TaskPriority.NORMAL));

        if (this.asyncConvertChildren) {
            await Promise.all(convertPromises);
            this.asyncConvertChildren = false;
        }

        if (!this.sdkData?.id) {
            // 对应的master可能已经被删除，直接返回
            return;
        }
        (this.presentationRef as IPresentationModel).masterChildrenMap[this.sdkData.id] = this.children
            .filter(child => !child.isPlaceholder);

        // 等待所有子元素convert完成，设置图片异步加载
        this.setSyncLoad();

        if (!convertRemoveChangeset) {
            // 防止mobx调用栈溢出
            // https://github.com/mobxjs/mobx/issues/2214
            this.childrenConverted = true;
        }
    }

    public updatePlaceholderFormat({ value: labelName, checked, targetShape }) {
        this.selectionService?.selectedWithCommit.markSpecialOperate(ESpecialOperationType.keep);
        transaction(() => {
            switch (labelName) {
                case TITLE:
                    this.handleTitlePlCheckedChange(checked, targetShape);
                    break;
                case BODY:
                    this.handleBodyPlCheckedChange(checked, targetShape);
                    break;
                case DATE:
                    this.handleDateCheckedChange(checked, targetShape);
                    break;
                case FOOTER:
                    this.handleFooterPlCheckedChange(checked, targetShape);
                    break;
                case NUMBER:
                    this.handleNumberPlCheckedChange(checked, targetShape);
                    break;
                default:
                    this.handleOtherCheckedChange(checked, targetShape);
                    break;
            }
        });
        this.selectionService?.selectedElements.clear();
    }

    public handleTitlePlCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            (this.sdkData as MasterModelSdkTypeV3)?.api.addPlaceholder(AddPlaceholderType.Title);
        } else {
            const titlePl = targetShape || this.findChildByPlTypeNum(STPlaceholderType.title)
                || this.findChildByPlTypeNum(STPlaceholderType.ctrTitle);
            const titlePlSdkData = titlePl?.sdkData;
            if (!titlePlSdkData) return;
            this.handleUnCheckedChange(titlePlSdkData);
            this.unlinkMatchedShapesOfPlaceholder(titlePlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_TITLE_CHECKBOX_CLICK,
        });
    }

    public handleBodyPlCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            (this.sdkData as MasterModelSdkTypeV3)?.api.addPlaceholder(AddPlaceholderType.Body);
        } else {
            const plTypeNum = STPlaceholderType.body;
            const bodyPlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(bodyPlSdkData);
            this.unlinkMatchedShapesOfPlaceholder(bodyPlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_TEXT_CHECKBOX_CLICK,
        });
    }

    public handleDateCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            (this.sdkData as MasterModelSdkTypeV3)?.api.addPlaceholder(AddPlaceholderType.Date);
        } else {
            const plTypeNum = STPlaceholderType.dt;
            const datePlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(datePlSdkData);
            this.unlinkMatchedShapesOfPlaceholder(datePlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_DATE_CHECKBOX_CLICK,
        });
    }

    public handleFooterPlCheckedChange(checked, targetShape) {
        if (checked) {
            (this.sdkData as MasterModelSdkTypeV3)?.api.addPlaceholder(AddPlaceholderType.Footer);
        } else {
            const plTypeNum = STPlaceholderType.ftr;
            const footerPlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(footerPlSdkData);
            this.unlinkMatchedShapesOfPlaceholder(footerPlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_FOOTER_CHECKBOX_CLICK,
        });
    }

    public handleNumberPlCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            (this.sdkData as MasterModelSdkTypeV3)?.api.addPlaceholder(AddPlaceholderType.SlideNumber);
        } else {
            const plTypeNum = STPlaceholderType.sldNum;
            const numberPlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(numberPlSdkData);
            this.unlinkMatchedShapesOfPlaceholder(numberPlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_PAGE_NUM_CHECKBOX_CLICK,
        });
    }

    public handleOtherCheckedChange(checked, targetShape) {
        if (checked) {
            // 暂时不支持添加其他占位符
        } else {
            // 暂时不支持通过children遍历方式查找其他类型的占位符，只能通过传值指定
            const otherPlSdkData = targetShape;
            this.handleUnCheckedChange(otherPlSdkData);
        }
    }

    public getTheme(): IThemeModel | undefined {
        return (this.parent as IPresentationModel).themeList.find(e => e.id === this.themeId);
    }

    private handleUnCheckedChange(plSdkData) {
        if (!plSdkData) return;
        this.deleteShapes([plSdkData]);
    }

    private unlinkMatchedShapesOfPlaceholder(plSdkData) {
        (this.sdkData as MasterModelSdkTypeV3)?.api.unlinkMatchedShapesOfPlaceholder(plSdkData);
    }
}
